import React, { useState } from 'react';

import { GoToDetailsPageDialog } from '@components/admin/booking/GoToDetailsPageDialog';
import { FormTitle } from '@components/admin/Forms';
import { Button } from '@components/Button';
import { Dialog } from '@components/Dialog';
import { ErrorMessage } from '@components/Forms/ErrorMessage';
import { TextInput } from '@components/Forms/TextInput';
import { SpinnerModal } from '@components/SpinnerModal';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { usePatchBookingInquiryMemo } from '@hooks/mutation/dashboard/usePatchBookingInquiryMemo';
import { usePatchBookingInquiryStatus } from '@hooks/mutation/dashboard/usePatchBookingInquiryStatus';
import { usePostBookingInquirySalespersonChina } from '@hooks/mutation/dashboard/usePostBookingInquirySalespersonChina';
import { usePostBookingInquirySalespersonJapan } from '@hooks/mutation/dashboard/usePostBookingInquirySalespersonJapan';
import { useUser } from '@hooks/queries/useUser';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { AdminInquiryDetail, allInquiryStatus } from '@models/BookingInquiry';
import { Country } from '@models/Country';
import { Role, rolesByCountry } from '@models/Role';
import { UserHelper } from '@models/User';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { FormError } from '@utils/errors';
import { createValidate, formStrings, requiredStringField, stringField } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { formatDateTime } from '@utils/types';
import clsx from 'clsx';
import { Form, Formik } from 'formik';
import { isRight } from 'fp-ts/lib/Either';
import * as t from 'io-ts';
import { Prompt, useHistory } from 'react-router-dom';
import { unwrap } from 'tyrann-io';

const strings = createStrings({
  ja: {
    infoManagement: '管理情報',
    id: 'D2D番号',
    createdAt: '受付時間',
    status: 'ステータス',
    admissionDate: '入会時間',
    memo: 'メモ',
    edit: '編集',
    csManagerJapan: 'CS担当者（日本）',
    csManagerChina: 'CS担当者（中国）',
    becomeCsManager: '担当者になる',
    changeSalesperson: '担当者があなたに変更されますが、進めてよろしいでしょうか。',
    statusUpdated: 'ステータスを更新しました。',
    memoUpdated: 'メモを更新しました',
    statusOptions: () =>
      allInquiryStatus.map((value, index) => ({ value: index.toString(), label: value })),
    reason: '理由：',
    notAuthorized: (csManagerByCountry: string) => `${csManagerByCountry}になる権限はありません。`,
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  managementContainer: {
    ...common.flexColumn,
    border: '1px solid #DDDDDD',
  },
  managementCell: {
    ...common.flexColumn,
    padding: '22px 22px 16px',
    '&:not(:last-child)': {
      borderBottom: '1px solid #DDDDDD',
    },
  },
  keyValueRow: {
    ...common.flexInline,
    ...common.middleText,
    color: theme.palette.grey['700'],
    height: 36,
    '& > div': {
      ...common.flexInline,
      alignItems: 'center',
    },
  },
  managementTitle: {
    ...common.middleText,
    ...common.flexInline,
    color: theme.palette.grey['800'],
    fontWeight: 500,
    marginBottom: '0.5rem',
  },
  managementForm: {
    ...common.flexColumn,
  },
  managementButton: {
    alignSelf: 'center',
    width: 160,
    margin: '0.5rem 0',
  },
  cancelButton: {
    alignSelf: 'center',
    width: 160,
    margin: '0.5rem 0',
    backgroundColor: theme.palette.grey['500'],
    color: theme.palette.primary.contrastText,
  },
  updatedInfo: {
    ...common.smallerText,
    color: theme.palette.primary.main,
    fontWeight: 500,
    marginTop: '0.1rem',
  },
  updateButton: {
    alignSelf: 'left',
    width: 60,
    margin: '0.5rem 0',
  },
  inlineButton: {
    height: 32,
    width: 130,
    borderRadius: 16,
  },
  formButton: {
    width: 70,
    height: 32,
    borderRadius: 16,
    margin: '1.5rem 1rem',
  },
  loadingArea: {
    height: '70vh',
    border: '1px solid #DDDDDD',
  },
  inlineForm: {
    flex: 1,
    '& > * + *': {
      marginTop: '0.5rem',
    },
  },
  input: {
    ...common.smallerText,
  },
  cancelReason: {
    ...common.smallerText,
    lineHeight: 2.5,
    paddingLeft: '0.5rem',
  },
  textInput: {
    padding: 5,
  },
  menuItemRoot: {
    borderColor: theme.palette.grey['800'],
  },
  dialogText: {
    ...common.middleText,
    ...common.fontWeight400,
    ...common.textGray800,
    textAlign: 'center',
    marginTop: 20,
    marginBottom: 60,
  },
}));

export interface InfoManagementProps {
  bookingInquiry: AdminInquiryDetail;
}

const memoSchema = t.type({
  memo: stringField(bookingInquiryStrings.memo, 0, 512),
});

const validateMemo = createValidate(memoSchema);

const inquiryNewStatusSchema = t.type({
  status: requiredStringField(strings.status),
});

const validateNewStatus = createValidate(inquiryNewStatusSchema);

export const InfoManagement: React.FC<InfoManagementProps> = (props) => {
  const { bookingInquiry } = props;
  const history = useHistory();

  const [showSalesperson, setShowSalesperson] = useState<boolean>(false);
  const [showNewStatusUpdated, setNewShowStatusUpdated] = useState<boolean>(false);
  const [showMemoUpdated, setShowMemoUpdated] = useState<boolean>(false);
  const [salespersonCountry, setSalespersonCountry] = useState<Country>();
  const [gotoDialogOpen, setGotoDialogOpen] = useState(false);

  const user = useUser();

  const postInquirySalespersonChina = usePostBookingInquirySalespersonChina(bookingInquiry.d2d_no);
  const postInquirySalespersonJapan = usePostBookingInquirySalespersonJapan(bookingInquiry.d2d_no);
  const patchInquiryStatus = usePatchBookingInquiryStatus(bookingInquiry.d2d_no);
  const patchInquiryMemo = usePatchBookingInquiryMemo(bookingInquiry.d2d_no);

  const notAuthorizedAlert = useConfirmDialog();

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const isUpdating = [
    postInquirySalespersonChina.isLoading,
    postInquirySalespersonJapan.isLoading,
    patchInquiryStatus.isLoading,
    patchInquiryMemo.isLoading,
  ].some(Boolean);

  const d2dNo = bookingInquiry.d2d_no;

  const isSalespersonChina = user.data?.id === bookingInquiry.salesperson_china?.id;
  const isSalespersonJapan = user.data?.id === bookingInquiry.salesperson_japan?.id;
  const isSalesperson = isSalespersonChina || isSalespersonJapan;

  const renderKeyValue = (key: string, value: React.ReactNode) => {
    return (
      <div className={classes.keyValueRow}>
        <div style={{ flex: 1 }}>{key}</div>
        <div style={{ flex: 1, letterSpacing: -1 }}>{value}</div>
      </div>
    );
  };

  const renderSalesperson = (country: Country) => {
    return (
      <>
        {country === 'cn'
          ? renderKeyValue(
              strings.csManagerChina,
              bookingInquiry.salesperson_china
                ? new UserHelper(bookingInquiry.salesperson_china).fullname
                : '-',
            )
          : renderKeyValue(
              strings.csManagerJapan,
              bookingInquiry.salesperson_japan
                ? new UserHelper(bookingInquiry.salesperson_japan).fullname
                : '-',
            )}
        {!(country === 'cn' ? isSalespersonChina : isSalespersonJapan) && (
          <Button
            color="primary"
            shape="round"
            size="small"
            className={classes.managementButton}
            text={strings.becomeCsManager}
            onClick={() => {
              if (
                rolesByCountry[country].includes(user.data?.role as Role) ||
                user.data?.is_superuser
              ) {
                setSalespersonCountry(country);
                setShowSalesperson(true);
              } else {
                notAuthorizedAlert.confirm({
                  message: strings.notAuthorized(
                    country === 'cn' ? strings.csManagerChina : strings.csManagerJapan,
                  ),
                });
              }
            }}
          />
        )}
        <Dialog open={showSalesperson}>
          <div className={classes.dialogText}>{strings.changeSalesperson}</div>
          <Button
            color="custom"
            shape="round"
            size="small"
            className={classes.cancelButton}
            text={formStrings.cancel}
            onClick={() => setShowSalesperson(false)}
            type="submit"
          />
          <Button
            color="primary"
            shape="round"
            size="small"
            className={classes.managementButton}
            text={formStrings.yes}
            onClick={() => {
              salespersonCountry === 'cn'
                ? postInquirySalespersonChina.mutateAsync()
                : postInquirySalespersonJapan.mutateAsync();
              setShowSalesperson(false);
            }}
            type="submit"
          />
        </Dialog>
      </>
    );
  };

  const renderMemo = () => {
    return (
      <Formik
        initialValues={{
          memo: bookingInquiry.memo,
        }}
        enableReinitialize
        validate={validateMemo}
        onSubmit={async (values) => {
          const validation = memoSchema.decode(values);

          if (!isRight(validation)) {
            throw new Error('InfoManagement: Unexpected erroneus form values. ');
          }

          try {
            await patchInquiryMemo.mutateAsync({
              memo: values.memo ?? '',
            });
            setShowMemoUpdated(true);
            setTimeout(() => setShowMemoUpdated(false), 5000);
          } catch (e) {
            if (e instanceof FormError) {
              // eslint-disable-next-line no-alert
              alert(e.getDisplayMessage());
            }
            throw e;
          }
        }}
      >
        {({ dirty, isSubmitting }) => (
          <Form className={classes.managementForm}>
            <Prompt when={dirty && !isSubmitting} message={() => formStrings.promptLeaving} />

            <div className={classes.managementTitle}>
              {strings.memo}
              {showMemoUpdated && (
                <>
                  <div style={{ width: '1rem' }} />
                  <div className={classes.updatedInfo}>{strings.memoUpdated}</div>
                </>
              )}
            </div>

            <TextInput
              multiline
              noLabel
              name="memo"
              itemVariant="column"
              itemErrorVariant="inline"
              classes={{
                input: classes.input,
                textInput: classes.textInput,
              }}
              disabled={!isSalesperson}
            />

            <div style={{ height: 10 }} />
            <ErrorMessage name={'memo'}></ErrorMessage>
            <Button
              color="primary"
              shape="round"
              size="small"
              className={classes.updateButton}
              text={formStrings.update}
              type="submit"
              disabled={!isSalesperson}
            />
          </Form>
        )}
      </Formik>
    );
  };

  const renderNewStatus = () => {
    const name = 'status';
    const closeDialog = () => {
      setGotoDialogOpen(false);
    };

    return bookingInquiry.status === '客側キャンセル済' ? (
      <>
        <div className={classes.managementTitle}>{strings.status}</div>

        <div className={classes.cancelReason}>
          <div className={clsx(classes.verticalSpace1, classes.textGray600)}>
            <span className={classes.textError}>{bookingInquiry.status}</span>
            <br />
            <span>{strings.reason}</span>

            {bookingInquiry.cancel_detail && (
              <>
                <br />
                <span style={{ whiteSpace: 'pre' }}>{bookingInquiry.cancel_detail}</span>
              </>
            )}
          </div>
        </div>
      </>
    ) : (
      <Formik
        initialValues={{ status: bookingInquiry.status }}
        onSubmit={async (_values) => {
          const values = unwrap(inquiryNewStatusSchema, _values);

          try {
            await patchInquiryStatus.mutateAsync(values as any);
            setNewShowStatusUpdated(true);
            setTimeout(() => setNewShowStatusUpdated(false), 5000);

            if (values.status === '進行中の案件を作成') {
              setGotoDialogOpen(true);
            }
          } catch (e) {
            if (e instanceof FormError) {
              // eslint-disable-next-line no-alert
              alert(e.getDisplayMessage());
            }
            throw e;
          }
        }}
        validate={validateNewStatus}
      >
        <>
          <GoToDetailsPageDialog
            open={gotoDialogOpen}
            d2dNo={d2dNo}
            onYes={() => {
              history.push(`${url('/admin/booking/BookingDetails')}?d2dNo=${d2dNo}&type=current`);
              closeDialog();
            }}
            onCancel={() => {
              closeDialog();
            }}
          />
          <Form className={classes.managementForm}>
            <div className={classes.managementTitle}>
              {strings.status}
              {showNewStatusUpdated && (
                <>
                  <div style={{ width: '1rem' }} />
                  <div className={classes.updatedInfo}>{strings.statusUpdated}</div>
                </>
              )}
            </div>
            <TextInput
              options={allInquiryStatus
                .filter((value) => value !== '客側キャンセル済')
                .map((value) => ({ label: value, value }))}
              noLabel
              name={name}
              classes={{
                input: classes.input,
                textInput: classes.textInput,
              }}
              choose
              disabled={!isSalesperson}
            />
            <div style={{ height: 10 }} />
            <Button
              color="primary"
              shape="round"
              size="small"
              className={classes.updateButton}
              text={formStrings.update}
              type="submit"
              disabled={!isSalesperson}
            />
          </Form>
        </>
      </Formik>
    );
  };

  return (
    <>
      <FormTitle title={strings.infoManagement} noEdit />
      <SpinnerModal open={isUpdating} />
      {notAuthorizedAlert.renderDialog()}
      <div className={classes.managementContainer}>
        <div className={classes.managementCell}>
          {renderKeyValue(strings.id, d2dNo)}
          {renderKeyValue(strings.createdAt, formatDateTime(bookingInquiry.created_at))}
          <div style={{ height: 8 }} />
        </div>

        <div className={classes.managementCell}>{renderSalesperson('cn')}</div>

        <div className={classes.managementCell}>{renderSalesperson('jp')}</div>

        <div className={classes.managementCell}>{renderMemo()}</div>

        <div className={classes.managementCell}>{renderNewStatus()}</div>
      </div>
    </>
  );
};
